import { Navigate, useSearchParams, useLocation } from 'react-router-dom'
import React, { useContext, useEffect, useState } from 'react'
import { firebase_auth, firebase_firestore } from '../../config/firebaseConfig'
import { collection, getDocs } from 'firebase/firestore'
import { GeneralContext } from '../contexts/GeneralContext'
import { signInWithCustomToken } from 'firebase/auth'
import ProgressCircle from '../components/ProgressCircle'
import { useExternalRedirect } from '../hooks/useExternalRedirect'
import useUserDetails from '../hooks/useUserDetails'
import useConditionalNavigation from '../hooks/navigation/useConditionalNavigation'

export default function GreenChartSubscriptionRouteGuard({ children }: any) {
  const [tokenLoading, setTokenLoading] = useState(false)
  const [allowedDomains, setAllowedDomains] = useState<string[]>([])
  const [redirectLink, setRedirectLink] = useState<string | null>(null)

  const [searchParams, setSearchParams] = useSearchParams()
  const { preserveUrlParams, checkAccountStatuses, setRepId, setTrialDays } =
    useContext(GeneralContext)

  const {
    user,
    loadingUser: loading,
    userDetails: usermeta,
    loadingUserDetails: loadingUserMeta,
  } = useUserDetails()

  const redirectToExternalSite = useExternalRedirect()

  const authToken = searchParams.get('authToken')
  const location = useLocation()

  // Authentication Token and not logged in
  if (!user && !loading && authToken && !tokenLoading) {
    signInWithCustomToken(firebase_auth, authToken)
      .then(() => {
        setTokenLoading(false)
      })
      .finally(() => {
        setTokenLoading(false)
      })
    setTokenLoading(true)
  }

  useEffect(() => {
    Promise.all([
      getDocs(collection(firebase_firestore, 'whitelistedAuthDomains')),
      getDocs(collection(firebase_firestore, 'tradingHouses')),
    ])
      .then(([whitelistSnapshot, tradingHouseSnapshot]) => {
        const domains: string[] = []
        whitelistSnapshot.forEach((doc) => {
          domains.push(doc.data().domain)
        })
        tradingHouseSnapshot.forEach((doc) => {
          const tradingHouse = doc.data()
          if (tradingHouse.domains) {
            domains.push(...tradingHouse.domains)
          }
        })
        setAllowedDomains(domains)
      })
      .catch((error) => {
        console.error('Error fetching domains:', error)
      })
  }, [])

  useEffect(() => {
    const repId = searchParams.get('repId')
    if (repId) {
      setRepId(repId)
    }

    const trialDays = searchParams.get('td')
    if (trialDays) {
      const trialDaysParsed = parseInt(trialDays)

      if (isNaN(trialDaysParsed) || trialDaysParsed > 30 || trialDaysParsed < 1) {
        return
      }

      setTrialDays(trialDaysParsed)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams])

  useEffect(() => {
    const checkRedirectLink = () => {
      const link: string | null = searchParams.get('redirectTo')
      let linkDomain: string | null = null

      if (link) {
        try {
          const url = new URL(link)
          linkDomain = url.protocol + '//' + url.hostname
        } catch (_) {
          // for non-urls, handle as a special case
          redirectToExternalSite(link) // might want this to be a different hook
        }
      }

      if (link && link.length && linkDomain && allowedDomains.includes(linkDomain)) {
        setRedirectLink(link)
      } else {
        setRedirectLink(null)
      }
    }
    checkRedirectLink()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, allowedDomains])

  useEffect(() => {
    if (usermeta) {
      checkAccountStatuses(usermeta)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usermeta])

  // Use the conditional navigation hook
  const conditionalNav = useConditionalNavigation()

  if ((tokenLoading || loading || loadingUserMeta) && !usermeta && !user) {
    return <ProgressCircle />
  }

  if (authToken) {
    searchParams.delete('authToken')
    setSearchParams(searchParams)
    return null
  }

  if (!user) {
    return <Navigate to={`/login${preserveUrlParams(searchParams)}`} replace />
  }

  if (redirectLink) {
    redirectToExternalSite(redirectLink)
    return null
  }

  if (location.pathname === '/dashboard') {
    if (conditionalNav.component) {
      return conditionalNav.component
    }
  }

  return children
}
